import { Box, Grid, Typography, useMediaQuery } from '@mui/material';
import AnnualBenefitCard from './Components/AnnualBenefitCard';
import { useEffect, useState } from 'react';
import { InfoOutlined } from '@mui/icons-material';
import { CustomButton } from '../../MUI_custom_component';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import SalesGrowthCard from './Components/SalesGrowthCard';
import BenefitStatusInfoCard from './Components/BenefitStatusInfoCard';
import { getAnnualTripDetails } from './APIs/action';
import TripCriteriaMetCard from './Components/TripCreteriaMetCard';
import { useBackrouteControl } from '../../../hooks/useSetBackroute';
import { BenefitType } from '../BenefitsRevamp/constants';

const AnnualTrip = ({}) => {
  const navigate = useNavigate();
  const { updateBackroute, resetBackroute } = useBackrouteControl();

  useEffect(() => {
    updateBackroute({
      customHandler: () =>
        navigate('/benefits/mybenefits', {
          state: { selectedTab: BenefitType.ANNUAL_BENEFIT },
          replace: true,
        }),
    });
    return () => resetBackroute();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const {
    selectedSupplier: selectedManufacturer,
    selectedEntity: selectedCompanyEntity,
    selectedSupplierData,
  } = useSelector((state) => state.entityReducer);
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down('lg'));

  const [annualTripData, setAnnualTripData] = useState(null);

  useEffect(() => {
    if (selectedManufacturer && selectedCompanyEntity) {
      fetchAnnualTripDetails();
    }
  }, [selectedManufacturer, selectedCompanyEntity]);

  const fetchAnnualTripDetails = async () => {
    const res = await getAnnualTripDetails(
      selectedManufacturer,
      selectedCompanyEntity,
    );
    if (res && !res.error) {
      setAnnualTripData(res?.data);
    }
  };

  const showHistory = useSelector(state => state.entityReducer.show_history);

  return (
    <>
      <Grid
        container
        spacing={{ xs: 2, sm: 2, md: 3, lg: 3 }}
        p={2}
      >
        {/* LEFT COLUMN */}
        <Grid
          item
          xs={12}
          lg={6}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {/* annual trip benefit card */}
            {annualTripData?.benefit_card && (
              <AnnualBenefitCard item={annualTripData?.benefit_card} />
            )}
            {/* benefit status info card */}
            {annualTripData?.benefit_status_info && (
              <BenefitStatusInfoCard
                data={annualTripData?.benefit_status_info}
              />
            )}
            {annualTripData?.trip_criteria_met_info && (
              <TripCriteriaMetCard
                item={annualTripData?.trip_criteria_met_info}
              />
            )}
            {annualTripData?.current_sales_progress && (
              <SalesGrowthCard
                sales_info={annualTripData?.current_sales_progress}
              />
            )}
          </Box>
        </Grid>

        {/* RIGHT COLUMN */}
        <Grid
          item
          xs={12}
          lg={6}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {/* Sales growth percentages */}
            {annualTripData?.sales_growth_info && (
              <Box
                sx={{
                  background: '#fff',
                  borderRadius: 2,
                  p: 2,
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    borderBottom: '1px solid #D9D9D9',
                    mb: 1,
                    pb: 0.5,
                  }}
                >
                  <Typography
                    variant='labelD'
                    mb={1}
                  >
                    {
                      annualTripData?.sales_growth_info?.header_info
                        ?.display_label
                    }
                  </Typography>
                  <Typography
                    variant='labelD'
                    mb={1}
                    sx={{
                      color:
                        annualTripData?.sales_growth_info?.header_info
                          ?.is_minimum_met === true
                          ? '#008000'
                          : '#CC0000',
                    }}
                  >
                    {annualTripData?.sales_growth_info?.header_info?.value}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <InfoOutlined
                      sx={{ fontSize: '16px', mr: 0.5, color: '#333' }}
                    />
                    <Typography
                      variant='labelM'
                      sx={{ color: '#333' }}
                    >
                      {
                        annualTripData?.sales_growth_info?.body_info
                          ?.display_label
                      }
                    </Typography>
                  </Box>
                  <Typography
                    variant='labelM'
                    sx={{ color: '#333' }}
                  >
                    {annualTripData?.sales_growth_info?.body_info?.value}
                  </Typography>
                </Box>
              </Box>
            )}

            {/* Allocation percentage card */}
            {annualTripData?.allocation_info && (
              <Box
                sx={{
                  border: '1px solid #995A0080',
                  borderRadius: 2,
                  p: 2,
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    borderBottom: '1px solid #D9D9D9',
                    mb: 1,
                    pb: 0.5,
                  }}
                >
                  <Typography
                    variant='labelD'
                    mb={1}
                  >
                    {isMobile
                      ? (() => {
                          const label =
                            annualTripData?.allocation_info?.current_allocation
                              ?.display_label ?? '';
                          const splitIdx = label.indexOf('(');
                          if (splitIdx === -1) return label;
                          return (
                            <>
                              {label.slice(0, splitIdx).trim()}
                              <br />
                              {label.slice(splitIdx)}
                            </>
                          );
                        })()
                      : annualTripData?.allocation_info?.current_allocation
                          ?.display_label}
                  </Typography>
                  <Typography
                    variant='labelD'
                    mb={1}
                    sx={{
                      color:
                        annualTripData?.allocation_info?.current_allocation
                          ?.is_minimum_met === true
                          ? '#008000'
                          : '#CC0000',
                    }}
                  >
                    {annualTripData?.allocation_info?.current_allocation?.value}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <InfoOutlined
                      sx={{ fontSize: '16px', mr: 0.5, color: '#333' }}
                    />
                    <Typography
                      variant='labelM'
                      sx={{ color: '#333' }}
                    >
                      {
                        annualTripData?.allocation_info?.required_allocation
                          ?.display_label
                      }
                    </Typography>
                  </Box>
                  <Typography
                    variant='labelM'
                    sx={{ color: '#333' }}
                  >
                    {
                      annualTripData?.allocation_info?.required_allocation
                        ?.value
                    }
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1 }}>
                  <CustomButton
                    variant='outlined'
                    color='primary'
                    size={'medium'}
                    sx={{ mt: { xs: 1, md: 0 }, width: '100%' }}
                    onClick={() =>
                      navigate('/inventory/company-inventory', {
                        state: {
                          companyId: selectedManufacturer,
                          entityId: selectedCompanyEntity,
                        },
                      })
                    }
                    label='Allocate Inventory'
                    disabled={showHistory}
                  />
                </Box>
              </Box>
            )}
          </Box>
        </Grid>
      </Grid>
    </>
  );
};

export default AnnualTrip;
